import React, { useEffect, useMemo, useRef, useState } from "react";
import { Mail, MapPin, Phone } from "lucide-react";
import Carousel from "../ui/Carousel";
import MapSection from "./MapSection";
import OrganizationHero from "./OrganizationHero";
import { Button } from "../ui/button";
import { FadeInUp, StaggerContainer, StaggerItem } from "../animations/AnimationComponents";
import { GradientOrb } from "../animations/BackgroundEffects";
import { useI18n } from "../../hooks/useI18n";
import type { OrganizationPageData as BusinessPageData } from "../../presentation/organizations/organizationPage.types";
import { selectDeterministicSample } from "../../domain/shared/commerce.utils";
import ProductCard from "./ProductCard";
import { getBusinessPath } from "../../routes/businessRoutes";
import ProductModal, { openProductOrder } from "./ProductModal";
import ServicesSection from "./ServicesSection";

const AutoCarousel: React.FC<{ images: { url: string; name?: string }[] }> = ({ images }) => {
  const [current, setCurrent] = useState(0);
  const roundImages = useMemo(
    () => selectDeterministicSample(images, 8, images.map(({ url }) => url).join("|")),
    [images]
  );
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (roundImages.length <= 1) return;
    timeoutRef.current = setTimeout(() => {
      if (current === roundImages.length - 1) {
        setCurrent(0);
      } else {
        setCurrent((value) => value + 1);
      }
    }, 3000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [current, roundImages]);

  useEffect(() => {
    setCurrent(0);
  }, [images]);

  return <Carousel images={roundImages} currentIndex={current} setCurrentIndex={setCurrent} />;
};

interface OrganizationHomePageProps {
  business: BusinessPageData;
  LinkComponent: React.ComponentType<{ to: string; children: React.ReactNode }>;
}

const OrganizationHomePage: React.FC<OrganizationHomePageProps> = ({ business, LinkComponent }) => {
  const { language, text, t } = useI18n();
  type ProductType = BusinessPageData["products"][number];
  const [modalProduct, setModalProduct] = useState<null | ProductType>(null);
  const contactItems = [
    { value: business.contactInfo.address, Icon: MapPin, label: text.contact.address },
    { value: business.contactInfo.phone, Icon: Phone, label: text.contact.phone },
    { value: business.contactInfo.email, Icon: Mail, label: text.contact.email },
  ].filter(({ value }) => value?.trim());
  const contactColumns = contactItems.length === 3
    ? "md:grid-cols-3"
    : contactItems.length === 2 ? "md:grid-cols-2" : "md:grid-cols-1";

  const getProductPreviewPrice = (product: ProductType) => {
    if (product.presentations && product.presentations.length > 0) {
      return t(text.business.from, { price: product.presentations[0].price });
    }
    return product.price || text.business.askPrice;
  };

  useEffect(() => {
    document.title = business ? `${business.name} | ApiNativos` : `ApiNativos`;
  }, [business]);

  const businessHeroImage = business.backgroundImage || business.logo;

  return (
    <>
      <OrganizationHero name={business.name} description={business.shortDescription} heroImage={businessHeroImage} height="large" />

      <section className="relative overflow-hidden bg-background py-16">
        <GradientOrb className="absolute right-10 top-10 -z-10" size="lg" color="gray" />
        <div className="container mx-auto max-w-4xl px-4">
          <FadeInUp delay={0.2}>
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-3xl font-bold text-foreground">{t(text.business.welcome, { name: business.name })}</h2>
              <div className="mx-auto mb-6 h-1 w-20 bg-primary"></div>
              <p className="text-lg leading-relaxed text-muted-foreground">{business.longDescription}</p>
            </div>
          </FadeInUp>

          <FadeInUp delay={0.4}>
            <div className="mt-8 flex justify-center gap-6">
              <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                <LinkComponent to={getBusinessPath(business.id, "productos")}>{text.business.viewProducts}</LinkComponent>
              </Button>
              <Button asChild className="text-white">
                <LinkComponent to={getBusinessPath(business.id, "contacto")}>{text.business.contactUs}</LinkComponent>
              </Button>
            </div>
          </FadeInUp>
        </div>
      </section>

      {business.products && business.products.length > 0 && (
        <section className="relative overflow-hidden bg-surface-cool py-16">
          <div className="container mx-auto max-w-5xl px-4">
            <FadeInUp delay={0.1}>
              <div className="mb-12 text-center">
                <h2 className="mb-2 text-3xl font-bold text-foreground">{text.business.catalogTitle}</h2>
                <p className="text-lg text-muted-foreground">{text.business.catalogBody}</p>
              </div>
            </FadeInUp>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3">
              {business.products.slice(0, 6).map((product, index) => (
                <FadeInUp key={product.id} delay={0.2 + index * 0.1}>
                  <ProductCard
                    product={product}
                    fallbackImage={business.logo}
                    previewPrice={getProductPreviewPrice(product)}
                    viewLabel={text.business.viewProduct}
                    actionLabel={product.presentations && product.presentations.length >= 2
                      ? text.business.choosePresentation
                      : product.presentations?.length === 1
                        ? text.business.consult
                        : text.business.askWhatsapp}
                    onAction={setModalProduct}
                    onPrimaryAction={(item) => item.presentations?.length ? setModalProduct(item) : openProductOrder(item, business.name, business.socialMedia.whatsapp, 1, 0, "featured")}
                    variant="featured"
                    whatsappAvailable={Boolean(business.socialMedia.whatsapp)}
                    primaryActionOpensWhatsApp={!product.presentations?.length}
                  />
                </FadeInUp>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Button asChild variant="outline" className="border-primary text-primary transition-transform hover:scale-105">
                <LinkComponent to={getBusinessPath(business.id, "productos")}>{text.business.viewFullCatalog}</LinkComponent>
              </Button>
            </div>
          </div>

          <ProductModal
            product={modalProduct}
            organizationName={business.name}
            whatsapp={business.socialMedia.whatsapp}
            fallbackImage={business.logo}
            onClose={() => setModalProduct(null)}
            mode="featured"
            labels={{
              weight: text.business.chooseVariantLabelWeight,
              flavor: text.business.chooseVariantLabelFlavor,
              presentation: text.business.presentation,
              action: text.business.askWhatsapp,
              multiPresentationHelp: text.business.multiPresentationHelp,
            }}
          />
        </section>
      )}

      {business.servicesVisible && business.services?.length ? (
        <ServicesSection
          services={business.services}
          fallbackImage={business.logo}
          title={language === "es" ? "Servicios" : "Services"}
          emptyTitle={language === "es" ? "No hay servicios disponibles" : "No services available"}
        />
      ) : null}

      <section className="relative overflow-hidden bg-surface-alt py-16">
        <GradientOrb className="absolute bottom-10 left-10 -z-10" size="md" color="gray" />
        <div className="container mx-auto px-4">
          <FadeInUp delay={0.1}>
            <div className="mb-12 text-center">
              <h2 className="mb-2 text-3xl font-bold text-foreground">{text.business.facilities}</h2>
              <p className="text-lg text-muted-foreground">{text.business.facilitiesBody}</p>
            </div>
          </FadeInUp>

          {business.images.length > 0 ? (
            <AutoCarousel images={business.images.slice(0, 6)} />
          ) : (
            <FadeInUp delay={0.3}>
              <div className="py-12 text-center">
                <p className="text-lg text-gray-500">{text.business.noFacilityImages}</p>
              </div>
            </FadeInUp>
          )}

          <FadeInUp delay={0.6}>
            <div className="mt-12 text-center">
              <Button asChild variant="outline" className="border-primary text-primary transition-transform hover:scale-105">
                <LinkComponent to={getBusinessPath(business.id, "galeria")}>{text.business.viewFullGallery}</LinkComponent>
              </Button>
            </div>
          </FadeInUp>
        </div>
      </section>

      <section className="relative overflow-hidden bg-background py-16">
        <GradientOrb className="absolute right-20 top-20 -z-10" size="sm" color="gray" />
        <div className="container mx-auto max-w-4xl px-4">
          <FadeInUp delay={0.2}>
            {contactItems.length > 0 && <div className="rounded-ui-lg border border-primary/15 bg-surface-cool p-4 shadow-ui-card sm:p-8">
              <h3 className="mb-6 text-center text-2xl font-bold">{text.business.howToFindUs}</h3>
              <StaggerContainer>
                <div className={`grid gap-4 ${contactColumns}`}>
                  {contactItems.map(({ value, Icon, label }) => (
                    <StaggerItem key={label} className="min-w-0">
                      <div className="flex items-center justify-center gap-3 rounded-ui-sm p-4 transition-colors duration-200 hover:bg-card/70">
                        <Icon aria-hidden="true" className="h-6 w-6 shrink-0 text-primary" />
                        <p className="min-w-0 break-words text-foreground/85">
                          <span className="sr-only">{label}: </span>{value}
                        </p>
                      </div>
                    </StaggerItem>
                  ))}
                </div>
              </StaggerContainer>
            </div>}
          </FadeInUp>
          <FadeInUp delay={0.3}>
            <div className="mt-6">
              {business.location ? (
                <div className="overflow-hidden rounded-ui-md shadow-ui-card">
                  <MapSection coordinates={{ latitude: business.location.lat, longitude: business.location.lng }} address={business.contactInfo.address} name={business.name} height="240px" />
                </div>
              ) : (
                <div className="mt-4 text-center text-muted-foreground">{text.business.locationUnavailable}</div>
              )}
            </div>
          </FadeInUp>
        </div>
      </section>

    </>
  );
};

export default OrganizationHomePage;
